import React, { useState, useEffect, useRef } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material"
import BluetoothIcon from "@mui/icons-material/Bluetooth"
import SearchIcon from "@mui/icons-material/Search"
import CloseIcon from "@mui/icons-material/Close"
import RestartAltIcon from "@mui/icons-material/RestartAlt"
import { useParams, useNavigate } from "react-router-dom"
import { useQuery, useQueryClient } from "react-query"
import BoardCanvas from "../components/BoardCanvas"
import { useHeaderSubtitle } from "../components/Layout"
import useStickyState from "../hooks/useStickyState"
import {
  fetchGym,
  createGym,
  clearGymSlot,
  NotFoundError,
} from "../utils/gymApi"
import {
  connectToBluetooth,
  sendClimbsToWall,
  clearWall,
  COLOR_OPTIONS,
  PALETTE_A,
  PALETTE_B,
  withAliases,
} from "../utils/bluetooth"

const HOLD_TYPES = ["START", "MIDDLE", "FINISH", "FOOT"]
const pickHoldTypes = (palette) =>
  Object.fromEntries(HOLD_TYPES.map((type) => [type, palette[type]]))
const DEFAULT_PALETTES = {
  A: pickHoldTypes(PALETTE_A),
  B: pickHoldTypes(PALETTE_B),
}

// Clamp user input to a valid color byte, falling back to the default
// while the field is empty or mid-edit
const toColorByte = (value, fallback) => {
  const n = Number(value)
  return value !== "" && Number.isFinite(n)
    ? Math.max(0, Math.min(255, Math.round(n)))
    : fallback
}

// Snap any stored value (old free-typed bytes included) onto one of the
// 64 dropdown options by dropping the LED-position bits
const toColorOption = (value, fallback) => toColorByte(value, fallback) & 252

const gradeAtAngle = (climb, angle) => {
  if (!climb?.stats?.length) {
    return null
  }
  const stat =
    climb.stats.find((s) => s.angle === angle) || climb.stats[0]
  return `${stat.display_difficulty} @ ${stat.angle}°`
}

const dotSx = (rgb, selected) => ({
  width: 18,
  height: 18,
  borderRadius: "50%",
  p: 0,
  flexShrink: 0,
  cursor: "pointer",
  border: selected ? "2px solid" : "1px solid",
  borderColor: selected ? "text.primary" : "divider",
  bgcolor: `rgb(${rgb.join(",")})`,
})

// A hold-type color as a small swatch dot; tapping it opens the full
// 8x8 grid of wall colors
function ColorDotPicker({ label, value, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const current = COLOR_OPTIONS.find((option) => option.byte === value)
  return (
    <>
      <Tooltip title={`${label}: ${current?.name ?? value}`}>
        <Box
          component="button"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={dotSx(current?.rgb ?? [0, 0, 0], false)}
        />
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(8, 20px)",
            gap: 0.5,
            px: 1,
            py: 0.5,
          }}
        >
          {COLOR_OPTIONS.map(({ byte, name, rgb }) => (
            <Box
              key={byte}
              component="button"
              title={name}
              onClick={() => {
                onChange(byte)
                setAnchorEl(null)
              }}
              sx={dotSx(rgb, byte === value)}
            />
          ))}
        </Box>
      </Menu>
    </>
  )
}

function SlotCard({
  slug,
  slot,
  data,
  onStateChange,
  palette,
  paletteValues,
  onPaletteChange,
  onPaletteReset,
  holdStyle,
}) {
  const navigate = useNavigate()

  const handleClear = async () => {
    const state = await clearGymSlot(slug, slot)
    onStateChange(state)
  }

  const grade = data && gradeAtAngle(data.climb, data.angle)

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="caption"
            noWrap
            sx={{ fontWeight: 700, flexGrow: 1, minWidth: 0 }}
          >
            {data
              ? data.climb?.name || data.name || "Custom selection"
              : `Problem ${slot}`}
            {grade && (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                {` · ${grade}`}
              </Typography>
            )}
          </Typography>
          <Tooltip title="Pick climb">
            <IconButton
              size="small"
              onClick={() =>
                navigate(`/search-climbs?gym=${slug}&slot=${slot}`)
              }
            >
              <SearchIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          {data && (
            <Tooltip title="Clear slot">
              <IconButton size="small" onClick={handleClear}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}
        >
          {HOLD_TYPES.map((type) => (
            <ColorDotPicker
              key={type}
              label={type.toLowerCase()}
              value={toColorOption(
                paletteValues[type],
                DEFAULT_PALETTES[slot][type]
              )}
              onChange={(byte) => onPaletteChange(type, byte)}
            />
          ))}
          <Tooltip title="Reset colors">
            <IconButton
              size="small"
              onClick={onPaletteReset}
              sx={{ ml: "auto" }}
            >
              <RestartAltIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
        {data ? (
          data.error === "climb_not_found" ? (
            <Typography color="error" variant="caption">
              This climb is no longer in the database.
            </Typography>
          ) : (
            <BoardCanvas
              climb={data.climb}
              palette={palette}
              holdStyle={holdStyle}
              maxHeight="40vh"
            />
          )
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "15vh",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Empty
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

function CreateGymPrompt({ slug, onCreated }) {
  const [name, setName] = useState("")
  const [isCreating, setIsCreating] = useState(false)

  const handleCreate = async () => {
    setIsCreating(true)
    try {
      const state = await createGym(slug, name || slug)
      onCreated(state)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Card variant="outlined" sx={{ maxWidth: 480, mx: "auto", mt: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          No gym here yet
        </Typography>
        <Typography color="text.secondary" gutterBottom>
          Create a page for the kilter board at <b>{slug}</b>? Anyone with
          this link will see what's currently on the wall.
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Display name (optional)"
          value={name}
          onChange={(event) => setName(event.target.value)}
          sx={{ my: 2 }}
        />
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={isCreating}
        >
          Create {slug}
        </Button>
      </CardContent>
    </Card>
  )
}

export default function Gym() {
  const { slug } = useParams()
  const queryClient = useQueryClient()
  const [bluetoothCharacteristic, setBluetoothCharacteristic] = useState(null)
  const [recentGyms, setRecentGyms] = useStickyState([], "recentGyms")
  const [palettes, setPalettes] = useStickyState(
    DEFAULT_PALETTES,
    "gymPalettes"
  )
  const [holdStyle, setHoldStyle] = useStickyState("circle", "gymHoldStyle")
  const lastSentRef = useRef(null)

  // Merge over the defaults so a stale localStorage entry missing a hold
  // type still yields a complete palette
  const paletteValuesForSlot = (slot) => ({
    ...DEFAULT_PALETTES[slot],
    ...palettes?.[slot],
  })

  const buildPalette = (slot) => {
    const values = paletteValuesForSlot(slot)
    return withAliases(
      Object.fromEntries(
        HOLD_TYPES.map((type) => [
          type,
          toColorByte(values[type], DEFAULT_PALETTES[slot][type]),
        ])
      )
    )
  }

  const setPaletteValue = (slot, type, value) =>
    setPalettes((prev) => ({
      ...prev,
      [slot]: { ...DEFAULT_PALETTES[slot], ...prev?.[slot], [type]: value },
    }))

  const resetPalette = (slot) =>
    setPalettes((prev) => ({ ...prev, [slot]: DEFAULT_PALETTES[slot] }))

  const { data: gym, error, isLoading } = useQuery(
    ["gym", slug],
    () => fetchGym(slug),
    {
      // Polling a missing gym would flip the query back to loading every
      // interval, remounting the create prompt and wiping its name field
      refetchInterval: (data, query) =>
        query.state.error instanceof NotFoundError ? false : 4000,
      refetchIntervalInBackground: true,
      retry: false,
    }
  )

  const setGymState = (state) => queryClient.setQueryData(["gym", slug], state)

  useHeaderSubtitle(gym?.name)

  useEffect(() => {
    if (!gym) {
      return
    }
    const entry = { slug: gym.slug, name: gym.name }
    setRecentGyms((recents) =>
      [entry, ...(recents || []).filter((g) => g.slug !== gym.slug)].slice(0, 5)
    )
  }, [gym?.slug, gym?.name])

  const slotEntries = ["A", "B"]
    .map((slot) => ({
      climb: gym?.slots?.[slot]?.climb,
      palette: buildPalette(slot),
    }))
    .filter((entry) => entry.climb)

  const updatedKey = `${gym?.slots?.A?.updated_at}|${gym?.slots?.B?.updated_at}`

  const sendToBoard = async () => {
    if (!bluetoothCharacteristic) {
      return
    }
    if (slotEntries.length === 0) {
      await clearWall(bluetoothCharacteristic)
    } else {
      await sendClimbsToWall(bluetoothCharacteristic, slotEntries)
    }
    lastSentRef.current = updatedKey
  }

  useEffect(() => {
    if (bluetoothCharacteristic && gym && lastSentRef.current !== updatedKey) {
      sendToBoard()
    }
  }, [bluetoothCharacteristic, updatedKey, gym])

  const handleConnectToBluetooth = async () => {
    try {
      const characteristic = await connectToBluetooth()
      if (!characteristic) {
        return
      }
      characteristic.service?.device?.addEventListener(
        "gattserverdisconnected",
        () => {
          // Force a resend on reconnect: the board may have been power
          // cycled and lost what was on it
          lastSentRef.current = null
          setBluetoothCharacteristic(null)
        }
      )
      setBluetoothCharacteristic(characteristic)
    } catch (error) {
      console.error("Error connecting to Bluetooth:", error)
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error instanceof NotFoundError) {
    return <CreateGymPrompt slug={slug} onCreated={setGymState} />
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ mt: 6 }}>
        Could not load this gym. Is the server running?
      </Typography>
    )
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          {!bluetoothCharacteristic ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<BluetoothIcon />}
              onClick={handleConnectToBluetooth}
            >
              Connect to board
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<BluetoothIcon />}
                disabled={slotEntries.length === 0}
                onClick={sendToBoard}
              >
                Send to board
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                onClick={() => clearWall(bluetoothCharacteristic)}
              >
                Blank board
              </Button>
            </>
          )}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={holdStyle}
            onChange={(event, value) => value && setHoldStyle(value)}
            sx={{ display: { xs: "none", sm: "inline-flex" } }}
          >
            <ToggleButton value="circle">Circles</ToggleButton>
            <ToggleButton value="fill">Fill</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <SlotCard
          slug={slug}
          slot="A"
          data={gym.slots?.A}
          onStateChange={setGymState}
          palette={buildPalette("A")}
          paletteValues={paletteValuesForSlot("A")}
          holdStyle={holdStyle}
          onPaletteChange={(type, value) => setPaletteValue("A", type, value)}
          onPaletteReset={() => resetPalette("A")}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <SlotCard
          slug={slug}
          slot="B"
          data={gym.slots?.B}
          onStateChange={setGymState}
          palette={buildPalette("B")}
          paletteValues={paletteValuesForSlot("B")}
          holdStyle={holdStyle}
          onPaletteChange={(type, value) => setPaletteValue("B", type, value)}
          onPaletteReset={() => resetPalette("B")}
        />
      </Grid>
    </Grid>
  )
}
